import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@direct-campaign/ui";
import { apiFetch } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

interface SurveyListItem {
  id: string;
  title: string;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  questionCount: number;
}

export function SurveysPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [surveys, setSurveys] = useState<SurveyListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function loadSurveys() {
    try {
      const res = await apiFetch("/api/surveys");
      if (res.ok) {
        const data = (await res.json()) as { surveys?: SurveyListItem[] };
        setSurveys(data.surveys ?? []);
      } else {
        setError("Failed to load surveys.");
      }
    } catch {
      setError("Failed to load surveys.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadSurveys();
  }, []);

  async function toggleActive(survey: SurveyListItem) {
    setBusyId(survey.id);
    setError("");
    const res = await apiFetch(`/api/surveys/${survey.id}`, {
      method: "PATCH",
      body: JSON.stringify({ isActive: !survey.isActive }),
    });
    setBusyId(null);
    if (res.ok) {
      void loadSurveys();
    } else {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      setError(data.error ?? "Failed to update survey.");
    }
  }

  async function handleDelete(id: string) {
    setBusyId(id);
    setError("");
    const res = await apiFetch(`/api/surveys/${id}`, { method: "DELETE" });
    setBusyId(null);
    setConfirmDeleteId(null);
    if (res.ok) {
      void loadSurveys();
    } else {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      setError(data.error ?? "Failed to delete survey.");
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Surveys</h2>
          <p className="text-muted-foreground mt-1">
            Build the question scripts canvassers walk through at the door.
          </p>
        </div>
        {isAdmin && (
          <Button asChild>
            <Link to="/dashboard/surveys/new">New survey</Link>
          </Button>
        )}
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40">
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Title</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Questions</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Status</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Updated</th>
              {isAdmin && (
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={isAdmin ? 5 : 4} className="px-4 py-8 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : surveys.length === 0 ? (
              <tr>
                <td colSpan={isAdmin ? 5 : 4} className="px-4 py-8 text-center text-muted-foreground">
                  No surveys yet.{isAdmin ? " Create your first survey." : ""}
                </td>
              </tr>
            ) : (
              surveys.map((s) => (
                <tr key={s.id} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-medium">{s.title}</p>
                    {s.description && (
                      <p className="text-muted-foreground text-xs mt-0.5 truncate max-w-md">
                        {s.description}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{s.questionCount}</td>
                  <td className="px-4 py-3">
                    <span
                      className={[
                        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                        s.isActive
                          ? "bg-green-50 text-green-700 border border-green-200 dark:bg-green-950/50 dark:text-green-400 dark:border-green-900"
                          : "bg-muted text-muted-foreground border",
                      ].join(" ")}
                    >
                      {s.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {new Date(s.updatedAt).toLocaleDateString()}
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-right">
                      {confirmDeleteId === s.id ? (
                        <span className="inline-flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">Delete?</span>
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={busyId === s.id}
                            onClick={() => void handleDelete(s.id)}
                          >
                            Confirm
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setConfirmDeleteId(null)}>
                            Cancel
                          </Button>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={busyId === s.id}
                            onClick={() => void toggleActive(s)}
                          >
                            {s.isActive ? "Deactivate" : "Activate"}
                          </Button>
                          <Button size="sm" variant="ghost" asChild>
                            <Link to={`/dashboard/surveys/${s.id}/edit`}>Edit</Link>
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setConfirmDeleteId(s.id)}
                          >
                            Delete
                          </Button>
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
